import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  addDays,
  addMonths,
  format,
  formatISO,
  subDays,
  subMonths,
} from 'date-fns'
import {
  HiOutlineCalendar,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineSquares2X2,
} from 'react-icons/hi2'
import { LuAsterisk } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Calendar from '@/components/common/Calendar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import Modal from '@/components/common/Modal'
import FilterContainer from '@/components/filter/FilterContainer'
import type {
  DueDateMacro,
  Macro,
  PostedDateMacro,
  Value,
} from '@/components/search/DateFilter/types'
import {
  DUE_DATE_MACROS,
  POSTED_DATE_MACROS,
} from '@/components/search/DateFilter/types'
import FilterChip from '@/components/search/FilterChip'

interface CustomDate {
  value: Value
  startFieldName: keyof Value
  endFieldName: keyof Value
  open: boolean
  setFieldValue: (field: string, value: string | boolean | null) => void
  setOpen: (open: boolean) => void
}

interface MacroButton {
  children: React.ReactNode
  onClick: () => void
}

interface DateFilter {
  value: Value
  setFieldValue: (field: string, value: string | boolean | null) => void
  className?: string
  containerClassName?: string
  filterChipProps?: Partial<React.ComponentProps<typeof FilterChip>>
}

const CustomDate: React.FC<CustomDate> = ({
  startFieldName,
  endFieldName,
  open,
  value,
  setFieldValue,
  setOpen,
}) => {
  const currentStart = useMemo(() => {
    if (value[startFieldName] === null) {
      return undefined
    }
    return new Date(String(value[startFieldName]))
  }, [startFieldName, value])

  const currentEnd = useMemo(() => {
    if (value[endFieldName] === null) {
      return undefined
    }
    return new Date(String(value[endFieldName]))
  }, [endFieldName, value])

  const handleSelectStart = (date: Date | undefined) => {
    setFieldValue(startFieldName, date ? formatISO(date) : null)
  }

  const handleSelectEnd = (date: Date | undefined) => {
    setFieldValue(endFieldName, date ? formatISO(date) : null)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-fit space-y-4"
        closeButtonClassName="top-0 right-3"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Custom Date</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col md:flex-row items-center gap-x-12 gap-y-6">
          <div className="flex flex-col gap-y-2">
            <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
              Start Date
            </span>
            <Calendar
              mode="single"
              selected={currentStart}
              onSelect={handleSelectStart}
              month={currentStart}
              initialFocus={true}
              showOutsideDays={false}
              className="p-0 -ml-1"
              // @ts-expect-error
              disabled={
                currentStart === undefined
                  ? { after: currentEnd }
                  : { before: currentStart, after: currentEnd }
              }
            />
          </div>
          <div className="flex flex-col gap-y-2">
            <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
              End Date
            </span>
            <Calendar
              mode="single"
              selected={currentEnd}
              onSelect={handleSelectEnd}
              month={currentEnd}
              initialFocus={true}
              showOutsideDays={false}
              highlightToday={false}
              className="p-0 -ml-1"
              // @ts-expect-error
              disabled={
                currentEnd === undefined
                  ? { before: currentStart }
                  : { before: currentStart, after: currentEnd }
              }
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const MacroButton: React.FC<MacroButton> = ({ children, onClick }) => {
  return (
    <button
      type="button"
      className="flex items-center gap-x-[6px] py-1 px-[3px] text-sm text-primary-900 dark:text-primary-300 rounded-md hover:bg-primary-100 dark:hover:bg-primary-800"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export const DueDateFilter: React.FC<DateFilter> = ({
  value,
  setFieldValue,
  className,
  filterChipProps,
}) => {
  const [selectedDisplay, setSelectedDisplay] = useState<string | null>(null)
  const [customDateModalIsOpen, setCustomDateModalIsOpen] =
    useState<boolean>(false)

  const today = useMemo(() => {
    return new Date()
  }, [])

  const dueDateMacros: Macro<DueDateMacro>[] = useMemo(() => {
    return [
      {
        title: 'Active only' as DueDateMacro,
        icon: HiOutlineCheckCircle,
        func: () => {
          // remove existing date filters if is active is true
          setFieldValue('active_after', null)
          setFieldValue('active_before', null)
          setFieldValue('is_active', true)
          setSelectedDisplay('Active only')
        },
      },
      {
        title: 'Next 30 days' as DueDateMacro,
        icon: HiOutlineCalendar,
        func: () => {
          const date = addDays(today, 30)
          // remove existing is_active filter if using date filters
          setFieldValue('is_active', null)
          setFieldValue('active_after', formatISO(today))
          setFieldValue('active_before', formatISO(date))
          setSelectedDisplay('Next 30 days')
        },
      },
      {
        title: 'Next 3 months' as DueDateMacro,
        icon: HiOutlineCalendarDays,
        func: () => {
          const date = addMonths(today, 3)
          // remove existing is_active filter if using date filters
          setFieldValue('is_active', null)
          setFieldValue('active_after', formatISO(today))
          setFieldValue('active_before', formatISO(date))
          setSelectedDisplay('Next 3 months')
        },
      },
      {
        title: 'Next 12 months' as DueDateMacro,
        icon: HiOutlineSquares2X2,
        func: () => {
          const date = addMonths(today, 12)
          // remove existing is_active filter if using date filters
          setFieldValue('is_active', null)
          setFieldValue('active_after', formatISO(today))
          setFieldValue('active_before', formatISO(date))
          setSelectedDisplay('Next 12 months')
        },
      },
      {
        title: 'Custom date' as DueDateMacro,
        icon: LuAsterisk,
        func: () => {
          // remove existing is_active filter if using date filters
          setFieldValue('is_active', null)
          setCustomDateModalIsOpen(!customDateModalIsOpen)
        },
      },
    ]
  }, [customDateModalIsOpen, today, setCustomDateModalIsOpen, setFieldValue])

  const handleDelete = useCallback(() => {
    setFieldValue('active_after', null)
    setFieldValue('active_before', null)
    setFieldValue('is_active', null)
    setSelectedDisplay(null)
  }, [setSelectedDisplay, setFieldValue])

  const formatDate = useCallback((date: string) => {
    return format(new Date(date), 'LLL do, yyyy')
  }, [])

  // NOTE: the below useEffects need to be separated, otherwise
  // changes to active_after and active_before will affect the is_active display,
  // and vice versa

  useEffect(() => {
    // if the user clears the is_active filter, reset the display to null
    if (value.is_active === null && selectedDisplay === 'Active only') {
      setSelectedDisplay(null)
    } else if (value.is_active === true && selectedDisplay === null) {
      setSelectedDisplay('Active only')
    }
  }, [value.is_active])

  useEffect(() => {
    // if the user clears the active_after and active_before filters, reset the display to null
    if (
      value.active_after === null &&
      value.active_before === null &&
      value.is_active === null
    ) {
      setSelectedDisplay(null)
      return
    }
    // if the user uses a macro, don't update the display with the underlying dates
    // (e.g. show the macro title instead)
    if (
      !customDateModalIsOpen &&
      selectedDisplay !== null &&
      DUE_DATE_MACROS.includes(selectedDisplay as DueDateMacro)
    ) {
      return
    }
    if (value.active_after === null && value.active_before !== null) {
      setSelectedDisplay(`Before ${formatDate(value.active_before)}`)
    } else if (value.active_after !== null && value.active_before === null) {
      setSelectedDisplay(`After ${formatDate(value.active_after)}`)
    } else if (value.active_after !== null && value.active_before !== null) {
      setSelectedDisplay(
        `${formatDate(value.active_after)} - ${formatDate(value.active_before)}`,
      )
    }
  }, [value.active_after, value.active_before])

  return (
    <FilterContainer
      title="Due Date"
      count={selectedDisplay ? 1 : 0}
      onClear={handleDelete}
      className={className}
    >
      <CustomDate
        startFieldName="active_after"
        endFieldName="active_before"
        open={customDateModalIsOpen}
        value={value}
        setFieldValue={setFieldValue}
        setOpen={setCustomDateModalIsOpen}
      />
      <div className="flex flex-col w-full gap-y-2">
        <div className="flex flex-col -mx-[4px]">
          {dueDateMacros.map((macro, index) => (
            <MacroButton key={index} onClick={macro.func}>
              <macro.icon />
              {macro.title}
            </MacroButton>
          ))}
        </div>
        {selectedDisplay && (
          <FilterChip
            variant="positive"
            content={selectedDisplay}
            onDelete={handleDelete}
            {...filterChipProps}
          />
        )}
      </div>
    </FilterContainer>
  )
}

export const PostedDateFilter: React.FC<DateFilter> = ({
  value,
  setFieldValue,
  className,
  filterChipProps,
}) => {
  const [selectedDisplay, setSelectedDisplay] = useState<string | null>(null)
  const [customDateModalIsOpen, setCustomDateModalIsOpen] =
    useState<boolean>(false)

  const today = useMemo(() => {
    return new Date()
  }, [])

  const postedDateMacros: Macro<PostedDateMacro>[] = useMemo(() => {
    return [
      {
        title: 'Past day' as PostedDateMacro,
        icon: HiOutlineClock,
        func: () => {
          const date = subDays(today, 1)
          setFieldValue('published_after', formatISO(date))
          setSelectedDisplay('Past day')
        },
      },
      {
        title: 'Past week' as PostedDateMacro,
        icon: HiOutlineCalendar,
        func: () => {
          const date = subDays(today, 7)
          setFieldValue('published_after', formatISO(date))
          setSelectedDisplay('Past week')
        },
      },
      {
        title: 'Past month' as PostedDateMacro,
        icon: HiOutlineCalendarDays,
        func: () => {
          const date = subMonths(today, 1)
          setFieldValue('published_after', formatISO(date))
          setSelectedDisplay('Past month')
        },
      },
      {
        title: 'Past year' as PostedDateMacro,
        icon: HiOutlineSquares2X2,
        func: () => {
          const date = subMonths(today, 12)
          setFieldValue('published_after', formatISO(date))
          setSelectedDisplay('Past year')
        },
      },
      {
        title: 'Custom date' as PostedDateMacro,
        icon: LuAsterisk,
        func: () => {
          setCustomDateModalIsOpen(!customDateModalIsOpen)
        },
      },
    ]
  }, [customDateModalIsOpen, today, setCustomDateModalIsOpen, setFieldValue])

  const handleDelete = useCallback(() => {
    setFieldValue('published_after', null)
    setFieldValue('published_before', null)
    setSelectedDisplay(null)
  }, [setSelectedDisplay, setFieldValue])

  const formatDate = useCallback((date: string) => {
    return format(new Date(date), 'LLL do, yyyy')
  }, [])

  useEffect(() => {
    // if the user clears the filter, reset the title to null
    if (value.published_after === null && value.published_before === null) {
      setSelectedDisplay(null)
      return
    }
    // if the user uses a macro, don't update the display with the underlying dates
    // (e.g. show the macro title instead)
    if (
      !customDateModalIsOpen &&
      selectedDisplay !== null &&
      POSTED_DATE_MACROS.includes(selectedDisplay as PostedDateMacro)
    ) {
      return
    }
    if (value.published_after === null && value.published_before !== null) {
      setSelectedDisplay(`Before ${formatDate(value.published_before)}`)
    } else if (
      value.published_after !== null &&
      value.published_before === null
    ) {
      setSelectedDisplay(`After ${formatDate(value.published_after)}`)
    } else if (
      value.published_after !== null &&
      value.published_before !== null
    ) {
      setSelectedDisplay(
        `${formatDate(value.published_after)} - ${formatDate(value.published_before)}`,
      )
    }
  }, [value.published_after, value.published_before])

  return (
    <FilterContainer
      title="Posted Date"
      count={selectedDisplay ? 1 : 0}
      onClear={handleDelete}
      className={className}
    >
      <CustomDate
        startFieldName="published_after"
        endFieldName="published_before"
        open={customDateModalIsOpen}
        value={value}
        setFieldValue={setFieldValue}
        setOpen={setCustomDateModalIsOpen}
      />
      <div className="flex flex-col w-full gap-y-2">
        <div className="flex flex-col -mx-[4px]">
          {postedDateMacros.map((macro, index) => (
            <MacroButton key={index} onClick={macro.func}>
              <macro.icon />
              {macro.title}
            </MacroButton>
          ))}
        </div>
        {selectedDisplay && (
          <FilterChip
            variant="positive"
            content={selectedDisplay}
            onDelete={handleDelete}
            {...filterChipProps}
          />
        )}
      </div>
    </FilterContainer>
  )
}

const DateFilter: React.FC<DateFilter> = ({
  value,
  setFieldValue,
  className,
  containerClassName,
  filterChipProps,
}) => {
  return (
    <div className={twMerge('flex flex-col', containerClassName)}>
      <DueDateFilter
        value={value}
        setFieldValue={setFieldValue}
        className={className}
        filterChipProps={filterChipProps}
      />
      <PostedDateFilter
        value={value}
        setFieldValue={setFieldValue}
        className={className}
        filterChipProps={filterChipProps}
      />
    </div>
  )
}

export default DateFilter
